import React, { useState, useMemo, useEffect } from 'react';
import { 
  FolderOpen, Plus, Edit2, Trash2, Search, 
  FileText, Link2, Image as ImageIcon, Video, File, Download,
  Eye, X, ExternalLink, Clock, Filter, Upload, CheckCircle2,
  MoreVertical, Grid3X3, List, ArrowUpDown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from 'sonner';
import { LastEditedBy } from '@/components/shared/LastEditedBy';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

// Download helper - handles relative URLs, blob URLs, and error responses
const downloadResource = async (resource) => {
  if (!resource?.url) {
    toast.error('No file URL available');
    return;
  }

  // Blob URLs are temporary and invalid after page refresh
  if (resource.url.startsWith('blob:')) {
    toast.error('File not available. Please re-upload.');
    return;
  }

  try {
    // Build full URL from relative path
    const fullUrl = resource.url.startsWith('http') 
      ? resource.url 
      : `${BASE_URL}${resource.url}`;
    
    const token = localStorage.getItem('token');
    const response = await fetch(fullUrl, { 
      headers: token ? { Authorization: `Bearer ${token}` } : {} 
    });
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    
    const blob = await response.blob();
    if (blob.size === 0) {
      throw new Error('Empty file');
    }
    
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = resource.fileName || resource.title || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
    toast.success('Download started');
  } catch (err) {
    console.error('Download error:', err);
    toast.error('Download failed - file mungkin tidak wujud di server');
  }
};

// Section Header - matches other pages
function SectionHeader({ icon: Icon, title, subtitle, action }) {
  return (
    <div className="px-6 py-4 border-b border-[hsl(var(--border))] bg-gradient-to-r from-[#09090B]/80 via-[#0a0a0c]/60 to-transparent relative overflow-hidden">
      <div className="absolute inset-0 opacity-20" style={{ background: 'linear-gradient(135deg, #E6394615, transparent 50%)' }} />
      <div className="relative flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-11 h-11 rounded-xl flex items-center justify-center shadow-lg"
            style={{ 
              background: 'linear-gradient(135deg, #E63946, #E6394690)',
              boxShadow: '0 4px 15px -3px #E6394650'
            }}>
            <Icon size={22} className="text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-white tracking-tight">{title}</h3>
            <p className="text-xs text-[#9CA3AF]">{subtitle}</p>
          </div>
        </div>
        {action}
      </div>
    </div>
  );
}

// File type helpers
const getFileIcon = (type, url) => {
  if (type === 'link') return Link2;
  if (type === 'image' || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return ImageIcon;
  if (type === 'video' || url?.match(/\.(mp4|mov|avi|webm)$/i)) return Video;
  if (url?.match(/\.(pdf)$/i)) return FileText;
  return File;
};

// File Icon Wrapper Component to avoid render-time component creation
function FileIconDisplay({ type, url, size, className }) {
  const color = getFileColor(type, url);
  if (type === 'link') return <Link2 size={size} style={{ color }} className={className} />;
  if (type === 'image' || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return <ImageIcon size={size} style={{ color }} className={className} />;
  if (type === 'video' || url?.match(/\.(mp4|mov|avi|webm)$/i)) return <Video size={size} style={{ color }} className={className} />;
  if (url?.match(/\.(pdf)$/i)) return <FileText size={size} style={{ color }} className={className} />;
  return <File size={size} style={{ color }} className={className} />;
}

const getFileColor = (type, url) => {
  if (type === 'link') return '#3B82F6';
  if (type === 'image' || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return '#10B981';
  if (type === 'video' || url?.match(/\.(mp4|mov|avi|webm)$/i)) return '#E63946';
  if (url?.match(/\.(pdf)$/i)) return '#F97316';
  return '#6B7280';
};

const getFileLabel = (type, url) => {
  if (type === 'link') return 'Link';
  if (type === 'image' || url?.match(/\.(jpg|jpeg|png|gif|webp)$/i)) return 'Image';
  if (type === 'video' || url?.match(/\.(mp4|mov|avi|webm)$/i)) return 'Video';
  if (url?.match(/\.(pdf)$/i)) return 'PDF';
  return 'File';
};

// Format date
const formatDate = (dateString) => {
  const date = new Date(dateString);
  const day = date.getDate().toString().padStart(2, '0');
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const time = date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  return `${day} ${month} ${year}, ${time}`;
};

// Premium Resource Card - Grid View
function ResourceCardGrid({ resource, onEdit, onDelete, onPreview }) {
  const fileColor = getFileColor(resource.type, resource.url);
  const fileLabel = getFileLabel(resource.type, resource.url);
  const isImage = resource.type === 'image' || resource.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  
  return (
    <div className="group relative bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl overflow-hidden hover:border-[#E63946]/40 transition-all duration-300 hover:shadow-xl hover:shadow-[#E63946]/5">
      {/* Thumbnail / Preview Area */}
      <div 
        className="relative h-36 overflow-hidden cursor-pointer"
        onClick={() => onPreview(resource)}
      >
        {isImage && resource.url && !resource.url.startsWith('blob:') ? (
          <img 
            src={resource.url.startsWith('http') ? resource.url : `${BASE_URL}${resource.url}`} 
            alt={resource.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
          />
        ) : (
          <div 
            className="w-full h-full flex flex-col items-center justify-center gap-2"
            style={{ background: `linear-gradient(135deg, ${fileColor}20, ${fileColor}08)` }}
          >
            <FileIconDisplay type={resource.type} url={resource.url} size={48} className="opacity-70" />
            <span className="text-xs font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ color: fileColor, backgroundColor: `${fileColor}15` }}>{fileLabel}</span>
            {resource.fileName && <span className="text-[9px] text-[#6B7280] truncate max-w-[80%]">{resource.fileName}</span>}
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
          <Button size="sm" variant="secondary" className="bg-white/20 backdrop-blur-sm border-0 text-white hover:bg-white/30">
            <Eye size={14} className="mr-1" />
            Preview
          </Button>
        </div>
        
        {/* Type Badge */}
        <div className="absolute top-3 left-3">
          <Badge 
            className="text-[10px] px-2 py-0.5 font-semibold backdrop-blur-sm"
            style={{ backgroundColor: `${fileColor}90`, color: 'white' }}
          >
            {fileLabel}
          </Badge>
        </div>
        
        {/* Actions Menu */}
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="secondary" className="h-7 w-7 bg-black/50 backdrop-blur-sm border-0 hover:bg-black/70">
                <MoreVertical size={14} className="text-white" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
              <DropdownMenuItem onClick={() => onPreview(resource)} className="text-white cursor-pointer">
                <Eye size={14} className="mr-2" />Preview
              </DropdownMenuItem>
              {resource.type !== 'link' && (
                <DropdownMenuItem onClick={() => downloadResource(resource)} className="text-white cursor-pointer" data-testid="download-resource-btn">
                  <Download size={14} className="mr-2" />Download
                </DropdownMenuItem>
              )}
              <DropdownMenuItem onClick={() => onEdit(resource)} className="text-white cursor-pointer">
                <Edit2 size={14} className="mr-2" />Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[hsl(var(--border))]" />
              <DropdownMenuItem onClick={() => onDelete(resource.id)} className="text-[#E63946] cursor-pointer">
                <Trash2 size={14} className="mr-2" />Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Content */}
      <div className="p-4">
        <h4 className="text-sm font-semibold text-white truncate mb-1">{resource.title}</h4>
        {resource.description && (
          <p className="text-xs text-[#6B7280] line-clamp-2 mb-3">{resource.description}</p>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
            <Clock size={10} />
            {formatDate(resource.createdAt)}
          </span>
          
          {resource.type === 'link' && (
            <a 
              href={resource.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[10px] text-[#3B82F6] hover:underline flex items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              <ExternalLink size={10} />
              Open
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

// Premium Resource Card - List View
function ResourceCardList({ resource, onEdit, onDelete, onPreview }) {
  const fileColor = getFileColor(resource.type, resource.url);
  const fileLabel = getFileLabel(resource.type, resource.url);
  
  return (
    <div className="group flex items-center gap-4 p-4 bg-[hsl(var(--surface-elevated))] border border-[hsl(var(--border))] rounded-xl hover:border-[#E63946]/40 transition-all duration-300 hover:shadow-lg hover:shadow-[#E63946]/5">
      {/* Icon */}
      <div 
        className="w-12 h-12 rounded-lg flex items-center justify-center shrink-0 cursor-pointer transition-transform hover:scale-105"
        style={{ backgroundColor: `${fileColor}15` }}
        onClick={() => onPreview(resource)}
      >
        <FileIconDisplay type={resource.type} url={resource.url} size={24} />
      </div>
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <h4 className="text-sm font-semibold text-white truncate">{resource.title}</h4>
          <Badge 
            className="text-[9px] px-1.5 py-0 font-medium shrink-0"
            style={{ backgroundColor: `${fileColor}20`, color: fileColor }}
          >
            {fileLabel}
          </Badge>
        </div>
        {resource.description && (
          <p className="text-xs text-[#6B7280] truncate mb-1">{resource.description}</p>
        )}
        <div className="flex items-center gap-3">
          <span className="text-[10px] text-[#6B7280] flex items-center gap-1">
            <Clock size={10} />
            {formatDate(resource.createdAt)}
          </span>
          {resource.type === 'link' && (
            <a 
              href={resource.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[10px] text-[#3B82F6] hover:underline flex items-center gap-1"
            >
              <ExternalLink size={10} />
              {resource.url?.substring(0, 30)}...
            </a>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <Button 
          size="icon" 
          variant="ghost" 
          className="h-8 w-8 text-[#9CA3AF] hover:text-white hover:bg-[#E63946]/20"
          onClick={() => onPreview(resource)}
        >
          <Eye size={16} />
        </Button>
        {resource.type !== 'link' && (
          <Button 
            size="icon" 
            variant="ghost" 
            className="h-8 w-8 text-[#9CA3AF] hover:text-[#10B981] hover:bg-[#10B981]/20"
            onClick={() => downloadResource(resource)}
            data-testid="download-resource-list-btn"
          >
            <Download size={16} />
          </Button>
        )}
        <Button 
          size="icon" 
          variant="ghost" 
          className="h-8 w-8 text-[#9CA3AF] hover:text-white hover:bg-[#3B82F6]/20"
          onClick={() => onEdit(resource)}
        >
          <Edit2 size={16} />
        </Button>
        <Button 
          size="icon" 
          variant="ghost" 
          className="h-8 w-8 text-[#9CA3AF] hover:text-[#E63946] hover:bg-[#E63946]/20"
          onClick={() => onDelete(resource.id)}
        >
          <Trash2 size={16} />
        </Button>
      </div>
    </div>
  );
}

// Preview Modal
function PreviewModal({ resource, open, onClose }) {
  if (!resource) return null;
  
  const fileColor = getFileColor(resource.type, resource.url);
  const isImage = resource.type === 'image' || resource.url?.match(/\.(jpg|jpeg|png|gif|webp)$/i);
  const isVideo = resource.type === 'video' || resource.url?.match(/\.(mp4|mov|avi|webm)$/i);
  const isPDF = resource.url?.match(/\.(pdf)$/i);
  const isLink = resource.type === 'link';
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3 text-white">
            <div 
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: `${fileColor}20` }}
            >
              <FileIconDisplay type={resource.type} url={resource.url} size={20} />
            </div>
            <div>
              <span className="block">{resource.title}</span>
              <span className="text-xs text-[#9CA3AF] font-normal">{formatDate(resource.createdAt)}</span>
            </div>
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-auto">
          {resource.description && (
            <p className="text-sm text-[#9CA3AF] mb-4 p-4 bg-[hsl(var(--surface-elevated))] rounded-lg">
              {resource.description}
            </p>
          )}
          
          {(() => {
            // Resolve full URL for preview
            const previewUrl = resource.url?.startsWith('blob:') 
              ? null
              : resource.url?.startsWith('http') 
                ? resource.url 
                : `${BASE_URL}${resource.url}`;
            
            return (
            <div className="rounded-lg overflow-hidden border border-[hsl(var(--border))]">
            {isImage && previewUrl && (
              <img 
                src={previewUrl} 
                alt={resource.title}
                className="w-full h-auto max-h-[400px] object-contain bg-black/50"
              />
            )}
            
            {isVideo && previewUrl && (
              <video 
                src={previewUrl} 
                controls 
                className="w-full h-auto max-h-[400px]"
              />
            )}
            
            {isPDF && previewUrl && (
              <iframe 
                src={previewUrl}
                title={resource.title}
                className="w-full h-[400px]"
              />
            )}
            
            {isLink && (
              <div className="p-8 text-center">
                <Link2 size={48} className="mx-auto text-[#3B82F6] mb-4" />
                <p className="text-sm text-[#9CA3AF] mb-4">External Link</p>
                <a 
                  href={resource.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[#3B82F6] hover:underline break-all"
                >
                  {resource.url}
                </a>
              </div>
            )}
            
            {(!isImage && !isVideo && !isPDF && !isLink) || (!previewUrl && !isLink) ? (
              <div className="p-8 text-center">
                <File size={48} className="mx-auto text-[#6B7280] mb-4" />
                <p className="text-sm text-[#9CA3AF]">Preview not available for this file type</p>
                {resource.url?.startsWith('blob:') && (
                  <p className="text-xs text-[#F97316] mt-2">File URL expired. Please re-upload the file.</p>
                )}
              </div>
            ) : null}
          </div>
            );
          })()}
        </div>
        
        <DialogFooter className="border-t border-[hsl(var(--border))] pt-4">
          <Button variant="outline" onClick={onClose}>Close</Button>
          {resource.url && (
            isLink ? (
              <a href={resource.url} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#E63946] hover:bg-[#D62839]"><ExternalLink size={14} className="mr-2" />Open Link</Button>
              </a>
            ) : (
              <Button className="bg-[#E63946] hover:bg-[#D62839]" onClick={() => downloadResource(resource)} data-testid="preview-download-btn">
                <Download size={14} className="mr-2" />Download
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Add/Edit Modal
function ResourceModal({ open, onClose, onSave, editingResource }) {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'file',
    url: '',
    file: null
  });
  const [uploading, setUploading] = useState(false);
  
  React.useEffect(() => {
    if (open) {
      if (editingResource) {
        setFormData({
          title: editingResource.title || '',
          description: editingResource.description || '',
          type: editingResource.type || 'file',
          url: editingResource.url || '',
          file: null
        });
      } else {
        setFormData({ title: '', description: '', type: 'file', url: '', file: null });
      }
    }
  }, [open, editingResource]);
  
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isImage = file.type.startsWith('image/');
      const isVideo = file.type.startsWith('video/');
      setFormData(prev => ({ 
        ...prev, 
        file,
        title: prev.title || file.name,
        url: URL.createObjectURL(file),
        type: isImage ? 'image' : isVideo ? 'video' : 'file'
      }));
    }
  };
  
  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    if (formData.type === 'link' && !formData.url.trim()) {
      toast.error('Please enter a URL');
      return;
    }
    
    setUploading(true);
    
    let fileUrl = formData.url;
    let fileName = formData.title;
    let fileSize = 0;
    let fileType = formData.type;
    
    // Upload file to server if a file was selected
    if (formData.file) {
      try {
        const token = localStorage.getItem('token');
        const uploadData = new FormData();
        uploadData.append('file', formData.file);
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        const res = await axios.post(`${API}/upload/image`, uploadData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        fileUrl = res.data.url;
        fileSize = formData.file.size;
        fileName = formData.file.name;
      } catch (e) {
        toast.error('File upload failed');
        setUploading(false);
        return;
      }
    }
    
    const resourceData = {
      id: editingResource?.id || `res-${Date.now()}`,
      title: formData.title,
      description: formData.description,
      type: fileType,
      url: fileUrl,
      fileName: fileName,
      fileSize: fileSize,
      createdAt: editingResource?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
    
    onSave(resourceData);
    setUploading(false);
    onClose();
  };
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[hsl(var(--surface))] border-[hsl(var(--border))] max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <FolderOpen size={20} className="text-[#E63946]" />
            {editingResource ? 'Edit Resource' : 'Add Resource'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Type Selection */}
          <div>
            <Label className="text-[#9CA3AF] text-sm mb-2 block">Resource Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={formData.type !== 'link' ? 'default' : 'outline'}
                className={formData.type !== 'link' ? 'bg-[#E63946] hover:bg-[#D62839]' : ''}
                onClick={() => setFormData(prev => ({ ...prev, type: 'file' }))}
              >
                <Upload size={14} className="mr-2" />
                Upload File
              </Button>
              <Button
                type="button"
                variant={formData.type === 'link' ? 'default' : 'outline'}
                className={formData.type === 'link' ? 'bg-[#3B82F6] hover:bg-[#2563EB]' : ''}
                onClick={() => setFormData(prev => ({ ...prev, type: 'link' }))}
              >
                <Link2 size={14} className="mr-2" />
                Add Link
              </Button>
            </div>
          </div>
          
          {/* File Upload */}
          {formData.type !== 'link' && (
            <div>
              <Label className="text-[#9CA3AF] text-sm mb-2 block">Upload File</Label>
              <div className="border-2 border-dashed border-[hsl(var(--border))] rounded-lg p-6 text-center hover:border-[#E63946]/50 transition-colors">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  onChange={handleFileChange}
                  accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {formData.file ? (
                    <div className="flex items-center justify-center gap-2 text-[#10B981]">
                      <CheckCircle2 size={20} />
                      <span className="text-sm">{formData.file.name}</span>
                    </div>
                  ) : (
                    <>
                      <Upload size={32} className="mx-auto text-[#6B7280] mb-2" />
                      <p className="text-sm text-[#9CA3AF]">Click to upload or drag & drop</p>
                      <p className="text-xs text-[#6B7280] mt-1">PDF, Images, Videos, Documents</p>
                    </>
                  )}
                </label>
              </div>
            </div>
          )}
          
          {/* Link URL */}
          {formData.type === 'link' && (
            <div>
              <Label className="text-[#9CA3AF] text-sm mb-2 block">URL</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData(prev => ({ ...prev, url: e.target.value }))}
                placeholder="https://example.com/resource"
                className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white"
              />
            </div>
          )}
          
          {/* Title */}
          <div>
            <Label className="text-[#9CA3AF] text-sm mb-2 block">Title *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Resource name..."
              className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white"
            />
          </div>
          
          {/* Description */}
          <div>
            <Label className="text-[#9CA3AF] text-sm mb-2 block">Description</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description about this resource..."
              className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))] text-white min-h-[80px]"
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button 
            className="bg-[#E63946] hover:bg-[#D62839]"
            onClick={handleSave}
            disabled={uploading}
          >
            {uploading ? 'Saving...' : editingResource ? 'Update' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function ResourceLibraryContent({ ib }) {
  // Load resources from MongoDB
  const [resources, setResources] = useState([]);
  const [resourcesLoaded, setResourcesLoaded] = useState(false);
  const [editMeta, setEditMeta] = useState({});
  
  React.useEffect(() => {
    if (!ib?.id) return;
    setResourcesLoaded(false);
    setResources([]);
    const fetchResources = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/ib-data/${ib.id}/resources`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.data?.data?.resources) {
          setResources(res.data.data.resources);
        }
        if (res.data?.updated_by_name) {
          setEditMeta({ name: res.data.updated_by_name, time: res.data.updated_at });
        }
        setResourcesLoaded(true);
      } catch (e) { console.error('Failed to load resources:', e); }
    };
    fetchResources();
  }, [ib?.id]);
  
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [modalOpen, setModalOpen] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [previewResource, setPreviewResource] = useState(null);
  
  // Explicit save — only called after user actions
  const saveResources = React.useCallback((data) => {
    if (!ib?.id) return;
    const token = localStorage.getItem('token');
    axios.put(`${API}/ib-data/${ib.id}/resources`, { data: { resources: data } }, { headers: { Authorization: `Bearer ${token}` } }).catch((e) => console.error("Save failed:", e));
  }, [ib?.id]);
  
  const filteredResources = useMemo(() => {
    let result = [...resources];
    
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(r => 
        r.title.toLowerCase().includes(query) ||
        r.description?.toLowerCase().includes(query)
      );
    }
    
    if (filterType !== 'all') {
      result = result.filter(r => {
        if (filterType === 'file') return r.type === 'file' || r.type === 'image' || r.type === 'video';
        return r.type === filterType;
      });
    }
    
    result.sort((a, b) => {
      if (sortBy === 'newest') return new Date(b.createdAt) - new Date(a.createdAt);
      if (sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'name') return a.title.localeCompare(b.title);
      return 0;
    });
    
    return result;
  }, [resources, searchQuery, sortBy, filterType]);
  
  const handleSave = (resourceData) => {
    let newList;
    if (editingResource) {
      newList = resources.map(r => r.id === resourceData.id ? resourceData : r);
      toast.success('Resource updated successfully');
    } else {
      newList = [...resources, resourceData];
      toast.success('Resource added successfully');
    }
    setResources(newList);
    saveResources(newList);
    setEditingResource(null);
  };
  
  const handleEdit = (resource) => {
    setEditingResource(resource);
    setModalOpen(true);
  };
  
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this resource?')) {
      const newList = resources.filter(r => r.id !== id);
      setResources(newList);
      saveResources(newList);
      toast.success('Resource deleted successfully');
    }
  };
  
  const stats = useMemo(() => ({
    total: resources.length,
    files: resources.filter(r => r.type !== 'link').length,
    links: resources.filter(r => r.type === 'link').length
  }), [resources]);
  
  return (
    <div className="flex flex-col h-full" data-testid="resource-library-page">
      {/* Fixed Header - Standardized layout matching Channel Settings */}
      <header className="flex-shrink-0 px-8 py-6 border-b border-[hsl(var(--border))] bg-[#0A0A0B]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#E63946] to-[#F97316] flex items-center justify-center shadow-lg shadow-[#E63946]/20">
              <FolderOpen size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-tight">Resource Library</h1>
              <p className="text-sm text-[#9CA3AF]">Team reference materials & documentation for <span className="text-white font-medium">{ib?.name || 'Channel'}</span></p>
              <LastEditedBy name={editMeta.name} timestamp={editMeta.time} />
            </div>
          </div>
          
          <Button 
            className="bg-gradient-to-r from-[#E63946] to-[#F97316] hover:opacity-90 text-white"
            onClick={() => { setEditingResource(null); setModalOpen(true); }}
            data-testid="add-resource-btn"
          >
            <Plus size={16} className="mr-2" />
            Add Resource
          </Button>
        </div>
      </header>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-6 space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-[hsl(var(--surface))] rounded-xl p-4 border border-[hsl(var(--border))]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E63946]/20 flex items-center justify-center">
                  <FolderOpen size={20} className="text-[#E63946]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.total}</p>
                  <p className="text-xs text-[#9CA3AF]">Total Resources</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[hsl(var(--surface))] rounded-xl p-4 border border-[hsl(var(--border))]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#10B981]/20 flex items-center justify-center">
                  <File size={20} className="text-[#10B981]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.files}</p>
                  <p className="text-xs text-[#9CA3AF]">Files</p>
                </div>
              </div>
            </div>
            
            <div className="bg-[hsl(var(--surface))] rounded-xl p-4 border border-[hsl(var(--border))]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#3B82F6]/20 flex items-center justify-center">
                  <Link2 size={20} className="text-[#3B82F6]" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-white">{stats.links}</p>
                  <p className="text-xs text-[#9CA3AF]">Links</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Toolbar */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 flex-1">
              <div className="relative flex-1 max-w-md">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search resources..."
                  className="pl-10 bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white"
                  data-testid="search-resources-input"
                />
              </div>
              
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[130px] bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white" data-testid="filter-type-select">
                  <Filter size={14} className="mr-2 text-[#6B7280]" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                  <SelectItem value="all" className="text-white">All Types</SelectItem>
                  <SelectItem value="file" className="text-white">Files</SelectItem>
                  <SelectItem value="link" className="text-white">Links</SelectItem>
                </SelectContent>
              </Select>
              
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[140px] bg-[hsl(var(--surface))] border-[hsl(var(--border))] text-white" data-testid="sort-by-select">
                  <ArrowUpDown size={14} className="mr-2 text-[#6B7280]" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[hsl(var(--surface-elevated))] border-[hsl(var(--border))]">
                  <SelectItem value="newest" className="text-white">Newest First</SelectItem>
                  <SelectItem value="oldest" className="text-white">Oldest First</SelectItem>
                  <SelectItem value="name" className="text-white">Name (A-Z)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* View Toggle */}
            <div className="flex items-center bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg p-1">
              <Button
                size="sm"
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                className={`h-8 px-3 ${viewMode === 'grid' ? 'bg-[#E63946]' : 'text-[#6B7280]'}`}
                onClick={() => setViewMode('grid')}
                data-testid="grid-view-btn"
              >
                <Grid3X3 size={16} />
              </Button>
              <Button
                size="sm"
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                className={`h-8 px-3 ${viewMode === 'list' ? 'bg-[#E63946]' : 'text-[#6B7280]'}`}
                onClick={() => setViewMode('list')}
                data-testid="list-view-btn"
              >
                <List size={16} />
              </Button>
            </div>
          </div>
          
          {/* Resources Section */}
          <div className="bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-xl overflow-hidden">
            <SectionHeader 
              icon={FolderOpen}
              title="All Resources"
              subtitle={`${filteredResources.length} resource${filteredResources.length !== 1 ? 's' : ''} found`}
            />
            
            <div className="p-6">
              {filteredResources.length === 0 ? (
                <div className="text-center py-16">
                  <FolderOpen size={56} className="mx-auto text-[#3A3A3D] mb-4" />
                  <p className="text-[#6B7280] mb-2">No resources found</p>
                  <p className="text-xs text-[#4A4A4D] mb-6">Upload files or add links to get started</p>
                  <Button 
                    className="bg-[#E63946] hover:bg-[#D62839]"
                    onClick={() => { setEditingResource(null); setModalOpen(true); }}
                    data-testid="add-first-resource-btn"
                  >
                    <Plus size={14} className="mr-2" />
                    Add First Resource
                  </Button>
                </div>
              ) : viewMode === 'grid' ? (
                <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {filteredResources.map(resource => (
                    <ResourceCardGrid
                      key={resource.id}
                      resource={resource}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onPreview={setPreviewResource}
                    />
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredResources.map(resource => (
                    <ResourceCardList
                      key={resource.id}
                      resource={resource}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      onPreview={setPreviewResource}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modals */}
      <ResourceModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingResource(null); }}
        onSave={handleSave}
        editingResource={editingResource}
      />
      
      <PreviewModal
        resource={previewResource}
        open={!!previewResource}
        onClose={() => setPreviewResource(null)}
      />
    </div>
  );
}
